/**
 * condenseLog.ts — noise reduction for the Build log viewer
 * (LogJobDetailPage.tsx).
 *
 * Motivated by a real job log (2026-09-05, ~31,600 lines, 1.8 MB — the
 * user had to scp a .gz off the box because the raw log was unusable in
 * the dashboard). 76% of that log was Rust/cargo's own list-dump noise:
 * every C build-script crate (aws-lc-sys, ring, zstd-sys, …) prints its
 * *entire* "available CPUs for this architecture" table before reporting
 * one real error line. On top of that, cargo's `--message-format=json`
 * output and `cargo:rerun-if-env-changed=` bookkeeping are 100%
 * machine-readable and never help a human read a failure.
 *
 * Condensing collapses only CONSECUTIVE runs of these categories — never
 * scattered/non-adjacent repeats — so nothing that isn't provably
 * boilerplate-in-a-row gets hidden. Every redaction leaves a countable
 * marker line in its place; nothing is silently dropped, and the toggle
 * to "full log" always shows the byte-for-byte original.
 */

export interface CondenseResult {
  text: string;
  totalLines: number;
  shownLines: number;
}

type Category = "json" | "rerun" | "ebadengine";

const CATEGORY_LABEL: Record<Category, string> = {
  json: "cargo machine-readable JSON build lines",
  rerun: "cargo rerun-if-env-changed lines",
  ebadengine: "npm engine-mismatch warning lines",
};

// cargo --message-format=json noise: every crate emits a compiler-artifact
// and a build-script-executed frame, each a huge single-line JSON blob
// that duplicates the human-readable "   Compiling <crate> vX" line right
// next to it. `build-finished` (carries success/failure) is deliberately
// NOT matched here — it's signal.
const JSON_NOISE_RE = /^\{"reason":"(compiler-artifact|build-script-executed)"/;
const RERUN_ENV_RE = /^\s*cargo:rerun-if-env-changed=/;
const EBADENGINE_RE = /^npm WARN EBADENGINE\b/;

// A build tool dumping a list, one bare value per line — e.g. cargo's
// "cargo:warning= znver1" / "cargo:warning= znver2" / … CPU-name table,
// or its "warning: <crate>@<version>: <value>" restatement of the same
// build-script output once the build fails. Generic on purpose: any
// "key= short-token" repeated many times in a row is a list dump, not
// prose worth reading line by line.
const TOKEN_LINE_RE =
  /^(\s*(?:cargo:warning=|warning:\s+[\w.-]+@[\w.+-]+:)\s*)([A-Za-z0-9_.+:/-]{0,40})\s*$/;

const MIN_TOKEN_RUN = 4;
const MIN_DUP_RUN = 3;

/**
 * Collapse consecutive noise into single marker lines. Pure function —
 * same input always condenses the same way, so it's safe to memoize on
 * the raw log text alone.
 */
export function condenseLog(raw: string): CondenseResult {
  const lines = raw.split("\n");
  const out: string[] = [];

  let runCat: Category | "token" | "dup" | null = null;
  let runKey: string | null = null; // token run's "key=" prefix
  let runItems: string[] = [];
  let lastKeptLine: string | null = null;

  const flush = () => {
    if (runCat === "token" && runKey !== null) {
      if (runItems.length >= MIN_TOKEN_RUN) {
        out.push(
          `${runKey}${runItems[0]}  ⋯ ${runItems.length} similar values redacted (see full log) ⋯`,
        );
      } else {
        for (const v of runItems) out.push(`${runKey}${v}`);
      }
    } else if (runCat === "dup") {
      if (runItems.length >= MIN_DUP_RUN) {
        out.push(
          `${runItems[0]}  ⋯ repeated ${runItems.length}× (see full log) ⋯`,
        );
      } else {
        out.push(...runItems);
      }
    } else if (
      runCat === "json" ||
      runCat === "rerun" ||
      runCat === "ebadengine"
    ) {
      // Unlike the token/dup runs above, a single line in one of these
      // categories is STILL pure noise (machine-readable metadata /
      // bookkeeping) — no run-length threshold needed.
      out.push(`⋯ ${runItems.length} ${CATEGORY_LABEL[runCat]} redacted ⋯`);
    }
    runCat = null;
    runKey = null;
    runItems = [];
  };

  for (const line of lines) {
    const tokenMatch = TOKEN_LINE_RE.exec(line);
    if (tokenMatch) {
      const prefix = tokenMatch[1]!;
      const value = tokenMatch[2] ?? "";
      if (runCat === "token" && runKey === prefix) {
        runItems.push(value);
      } else {
        flush();
        runCat = "token";
        runKey = prefix;
        runItems = [value];
      }
      lastKeptLine = null;
      continue;
    }

    let cat: Category | null = null;
    if (JSON_NOISE_RE.test(line)) cat = "json";
    else if (RERUN_ENV_RE.test(line)) cat = "rerun";
    else if (EBADENGINE_RE.test(line)) cat = "ebadengine";

    if (cat) {
      if (runCat === cat) runItems.push(line);
      else {
        flush();
        runCat = cat;
        runItems = [line];
      }
      lastKeptLine = null;
      continue;
    }

    // Exact-duplicate-consecutive-line collapse — catches things the two
    // rules above miss, e.g. a build tool restating the identical error
    // line once per (failed) retry, back to back.
    if (runCat === "dup" && runItems[0] === line) {
      runItems.push(line);
      continue;
    }
    flush();
    if (line === lastKeptLine) {
      runCat = "dup";
      runItems = [lastKeptLine, line];
      lastKeptLine = null;
    } else {
      out.push(line);
      lastKeptLine = line;
    }
  }
  flush();

  return {
    text: out.join("\n"),
    totalLines: lines.length,
    shownLines: out.length,
  };
}
